import { isPlatformBrowser } from '@angular/common';
import { EnvironmentInjector, inject, runInInjectionContext } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { first } from 'rxjs/operators';
import { IRV, ISubPhoto, ITowVehicle } from '@curbnturf/entities';
import { AuthService } from './auth/auth.service';
import { UserApiService } from './backend-api/user-api.service';
import { RvType, RV_TYPES } from './listings/mock-listings.data';
import { createIdbKvStore } from './storage/idb-kv-store.util';

/**
 * Phase 3 real-backend wiring. All the functions below are plain (not class
 * methods) because every one of the ~7 existing call sites invokes them
 * synchronously outside any component constructor — converting to an
 * injectable service would mean touching every call site's reactive shape,
 * not just adding a provider. Angular's `runInInjectionContext` lets this
 * module reach real DI from plain functions instead, bound once at bootstrap
 * (see `bindMyRvBackend` and its APP_INITIALIZER call in app.config.ts) —
 * same "preserve the existing contract" approach as Phase 1's AuthService.
 *
 * Local reads stay synchronous and instant either way (this module's own
 * localStorage cache remains the source of truth for UI reads); the real
 * backend push/pull happens underneath, asynchronously, best-effort.
 */
let _injector: EnvironmentInjector | null = null;
export function bindMyRvBackend(injector: EnvironmentInjector): void {
  _injector = injector;
  // Wipe the local RV-profile cache on sign-out — a real (backend-linked)
  // user's profiles re-hydrate from the server via hydrateMyRvFromBackend()
  // on next sign-in; a guest's unsynced profiles are lost, matching the
  // "wipe everything" logout policy.
  runInInjectionContext(injector, () => {
    inject(AuthService).loggedOut$.subscribe(() => void myRvStore.delete(MY_RV_PROFILES_IDB_KEY));
  });
}

/** For the interim `userSvc.update()` PUT: a freshly-picked photo (still a
 *  base64 data: URL) must never ride along in that JSON body — that's what
 *  triggers "payload too large"; its real bytes go up moments later via the
 *  dedicated multipart upload once the RV has a real backend id. Falls back
 *  to whatever real URL was already synced for this field so the interim
 *  PUT doesn't flash a blank/missing photo; omits the field only when
 *  nothing real has ever been synced (brand-new profile/first-ever photo). */
function sanitizePhotoField(pending: ISubPhoto | undefined, previous: ISubPhoto | undefined): ISubPhoto | undefined {
  if (!pending?.key?.startsWith('data:')) return pending;
  return previous?.key && !previous.key.startsWith('data:') ? previous : undefined;
}

/** Sanitizes all three photo fields on a profile bound for the PUT body.
 *  `previous` defaults to the profile itself — correct for every entry in the
 *  array except the one actively being edited (passed its real pre-edit
 *  snapshot by the caller), since an untouched sibling's own current value
 *  IS its best-known "previous real url" (or, if it's itself still a pending
 *  data: URL from some other in-flight edit, there's nothing better to fall
 *  back to and it gets correctly omitted either way). */
function sanitizeProfileForSync(profile: IMyRvProfile, previous: IMyRvProfile = profile): IMyRvProfile {
  return {
    ...profile,
    photo: sanitizePhotoField(profile.photo, previous.photo),
    licensePhoto: sanitizePhotoField(profile.licensePhoto, previous.licensePhoto),
    towVehicle: profile.towVehicle
      ? { ...profile.towVehicle, photo: sanitizePhotoField(profile.towVehicle.photo, previous.towVehicle?.photo) }
      : profile.towVehicle,
  };
}

/** Push one profile's fields to the real IUser.rvs array. Matches an existing
 *  backend row by `id` (already-synced) or `syncId` (this profile's own
 *  identity, present even before it has a real id); appends if neither
 *  matches. On success, patches the real assigned `id` back into the local
 *  profile so future saves update in place instead of duplicating.
 *
 *  `previous` — the profile's pre-edit snapshot, when the caller has one (a
 *  brand-new profile has none) — drives `sanitizeProfileForSync`'s real-URL
 *  fallback for the one entry actually being edited; see its doc comment. */
function syncProfileToBackend(platformId: object, profile: IMyRvProfile, previous?: IMyRvProfile): void {
  if (!_injector) return;
  runInInjectionContext(_injector, () => {
    const auth = inject(AuthService);
    const userSvc = inject(UserApiService);
    (async () => {
      const currentUser = await firstValueFrom(auth.authSession$.pipe(first()));
      if (!currentUser?.id) return; // not signed in with a real backend account — stay local-only
      // Source the full RV list from this module's own local cache, not
      // `currentUser.rvs` — the session's user is only ever populated with
      // id/email/firstName/lastName/role (see applySession() in
      // data-access-cnt/auth/auth.service.ts), never `rvs`, so building the
      // PUT body from it would silently wipe every other RV on the backend
      // on every single save.
      // The caller (updateMyRvProfile/writeMyRv) already wrote `profile`'s
      // merge into this cache before calling here, so it's already present.
      const rvs = readMyRvProfiles(platformId).profiles.map(p =>
        p.syncId === profile.syncId && previous ? sanitizeProfileForSync(p, previous) : sanitizeProfileForSync(p),
      );
      const updatedUser = await firstValueFrom(userSvc.update({ ...currentUser, rvs }));
      const saved = updatedUser.rvs?.find(r => r.syncId && r.syncId === profile.syncId);
      if (saved?.id) {
        patchMyRvProfileLocal(platformId, profile.syncId ?? '', { id: saved.id } as Partial<IMyRv>);
        if (profile.photo?.key?.startsWith('data:')) {
          await uploadRvPhoto(platformId, profile.syncId ?? '', currentUser.id, saved.id, profile.photo.key);
        }
        if (profile.licensePhoto?.key?.startsWith('data:')) {
          await uploadRvLicensePhoto(platformId, profile.syncId ?? '', currentUser.id, saved.id, profile.licensePhoto.key);
        }
        if (profile.towVehicle?.photo?.key?.startsWith('data:')) {
          await uploadTowVehiclePhoto(platformId, profile.syncId ?? '', currentUser.id, saved.id, profile.towVehicle.photo.key);
        }
      }
    })().catch(err => console.error('[my-rv] backend sync failed', err));
  });
}

/** Uploads the RV's own photo (data URL → real multipart upload) once the RV has a real backend id. */
async function uploadRvPhoto(platformId: object, syncId: string, userId: number, rvId: number, dataUrl: string): Promise<void> {
  if (!_injector) return;
  await runInInjectionContext(_injector, async () => {
    const userSvc = inject(UserApiService);
    try {
      const blob = await (await fetch(dataUrl)).blob();
      const form = new FormData();
      form.append('files', blob, 'rv-photo.jpg');
      form.append('caption', '');
      form.append('cropLeft', '0');
      form.append('cropTop', '0');
      form.append('cropRight', '0');
      form.append('cropBottom', '0');
      form.append('zoom', '1');
      form.append('rotation', '0');
      const updatedUser = await firstValueFrom(userSvc.uploadRvImage(userId, rvId, form));
      const saved = updatedUser.rvs?.find(r => r.id === rvId);
      if (saved?.photo) patchMyRvProfileLocal(platformId, syncId, { photo: saved.photo } as Partial<IMyRv>);
    } catch (err) {
      console.error('[my-rv] photo upload failed', err);
    }
  });
}

/** Uploads the RV's license-plate photo (data URL → real multipart upload) once the RV has a real backend id. */
async function uploadRvLicensePhoto(platformId: object, syncId: string, userId: number, rvId: number, dataUrl: string): Promise<void> {
  if (!_injector) return;
  await runInInjectionContext(_injector, async () => {
    const userSvc = inject(UserApiService);
    try {
      const blob = await (await fetch(dataUrl)).blob();
      const form = new FormData();
      form.append('files', blob, 'rv-license-photo.jpg');
      form.append('caption', '');
      form.append('cropLeft', '0');
      form.append('cropTop', '0');
      form.append('cropRight', '0');
      form.append('cropBottom', '0');
      form.append('zoom', '1');
      form.append('rotation', '0');
      const updatedUser = await firstValueFrom(userSvc.uploadRvLicenseImage(userId, rvId, form));
      const saved = updatedUser.rvs?.find(r => r.id === rvId);
      if (saved?.licensePhoto) patchMyRvProfileLocal(platformId, syncId, { licensePhoto: saved.licensePhoto } as Partial<IMyRv>);
    } catch (err) {
      console.error('[my-rv] license photo upload failed', err);
    }
  });
}

/** Uploads the RV's tow vehicle photo (data URL → real multipart upload) once the RV has a real backend id. */
async function uploadTowVehiclePhoto(platformId: object, syncId: string, userId: number, rvId: number, dataUrl: string): Promise<void> {
  if (!_injector) return;
  await runInInjectionContext(_injector, async () => {
    const userSvc = inject(UserApiService);
    try {
      const blob = await (await fetch(dataUrl)).blob();
      const form = new FormData();
      form.append('files', blob, 'tow-vehicle-photo.jpg');
      form.append('caption', '');
      form.append('cropLeft', '0');
      form.append('cropTop', '0');
      form.append('cropRight', '0');
      form.append('cropBottom', '0');
      form.append('zoom', '1');
      form.append('rotation', '0');
      const updatedUser = await firstValueFrom(userSvc.uploadTowVehicleImage(userId, rvId, form));
      const saved = updatedUser.rvs?.find(r => r.id === rvId);
      // The server merges the new photo into the full towVehicle object (it
      // already has year/make/model/etc.) and returns that whole object —
      // use it as-is rather than re-merging against possibly-stale local state.
      if (saved?.towVehicle?.photo) {
        patchMyRvProfileLocal(platformId, syncId, { towVehicle: saved.towVehicle } as Partial<IMyRv>);
      }
    } catch (err) {
      console.error('[my-rv] tow vehicle photo upload failed', err);
    }
  });
}

/** Removes a profile from the real backend's IUser.rvs array (only meaningful
 *  once it has a real `id` — a never-synced local profile has nothing to
 *  remove server-side). Best-effort, fire-and-forget from the caller's view. */
function removeProfileFromBackend(platformId: object, rvId: number | undefined): void {
  if (!_injector || !rvId) return;
  runInInjectionContext(_injector, () => {
    const auth = inject(AuthService);
    const userSvc = inject(UserApiService);
    (async () => {
      const currentUser = await firstValueFrom(auth.authSession$.pipe(first()));
      if (!currentUser?.id) return;
      // Caller (deleteMyRvProfile) already removed this profile from the
      // local cache before calling here, so this list is already correct —
      // see syncProfileToBackend's comment for why we source from here and
      // not `currentUser.rvs`.
      const rvs = readMyRvProfiles(platformId).profiles.map(p => sanitizeProfileForSync(p));
      await firstValueFrom(userSvc.update({ ...currentUser, rvs }));
    })().catch(err => console.error('[my-rv] backend delete failed', err));
  });
}

/**
 * Replace the local profile list with the signed-in user's real IUser.rvs,
 * so RV data set up on another device shows up here too. Called once from
 * app.config.ts, fire-and-forget (must never block app bootstrap).
 *
 * Waits for the first `user$` emission with a real backend id — NOT just the
 * first emission outright (a fresh, not-yet-signed-in visit's first emission
 * is `undefined`, and the sign-in that follows fires a *later* emission this
 * needs to react to, not just whatever state existed at page-load). Also
 * deliberately NOT `currentUserOnce$`, whose `skipWhile` never emits at all
 * for a visitor who stays logged out — this must resolve either way so the
 * fire-and-forget call above doesn't leak a dangling subscription forever.
 *
 * Once an id is known, fetches the user fresh via `UserService` rather than
 * trusting `authSession$`'s own `.rvs` — the session reflects whatever
 * Cognito's restored session (or a just-completed sign-in) carried, which
 * can be a stale copy from a previous session predating a just-added RV.
 *
 * A no-op for logged-out visitors or accounts with no saved RVs yet
 * (existing local/anonymous profiles are left alone rather than wiped for a
 * false "empty").
 */
export async function hydrateMyRvFromBackend(platformId: object): Promise<void> {
  if (!_injector || !isPlatformBrowser(platformId)) return;
  await runInInjectionContext(_injector, async () => {
    const auth = inject(AuthService);
    const userSvc = inject(UserApiService);
    const sessionUser = await firstValueFrom(auth.authSession$.pipe(first(user => !!user?.id)));
    if (!sessionUser?.id) return;
    const freshUser = await firstValueFrom(userSvc.fetch({ id: sessionUser.id }));
    if (!freshUser?.rvs || freshUser.rvs.length === 0) return;
    // Backfill a syncId for any RV that somehow still lacks one (e.g. a
    // pre-migration row) instead of letting an undefined syncId collide
    // across profiles and silently break Edit/delete/set-active by identity.
    // A plain spread (not normalizeProfile(), which narrows to the local
    // legacy-storage field set) so every real backend field — id included —
    // survives the hydrate.
    const profiles = freshUser.rvs.map(rv => (typeof rv.syncId === 'string' ? rv : { ...rv, syncId: newRvId() }));
    const activeFromServer = freshUser.selectedRv?.syncId ?? profiles[0]?.syncId ?? null;
    writeMyRvProfiles(platformId, { profiles, activeId: activeFromServer });
  });
}

/** Legacy single-RV localStorage key — still read once for one-time migration. */
const MY_RV_KEY = 'cnt-my-rv';
/** Legacy multi-profile localStorage key, migrated once into IndexedDB. */
const MY_RV_PROFILES_KEY = 'cnt-my-rv-profiles';
/** Current key, inside the shared IndexedDB store. */
const MY_RV_PROFILES_IDB_KEY = 'my-rv:profiles';

/**
 * Backed by the shared IndexedDB store instead of `localStorage` — this key
 * had a live `QuotaExceededError` fallback below (photos silently dropped)
 * that's the same failure mode already fixed for listing drafts in
 * `draft-store.util.ts`. `readMyRvProfiles()`/etc. below stay synchronous
 * against this store's in-memory cache so none of this module's ~7 existing
 * call sites need to become async; `initMyRvStore()` is wired into
 * `app.config.ts`'s `APP_INITIALIZER` chain, ahead of `bindMyRvBackend`/
 * `hydrateMyRvFromBackend`, so the cache is hydrated before backend sync can
 * write into it.
 */
export const myRvStore = createIdbKvStore<IMyRvProfilesState>({
  legacyKeys: { [MY_RV_PROFILES_IDB_KEY]: MY_RV_PROFILES_KEY },
  reviveLegacy: (_idbKey, parsed) => reviveMyRvProfilesState(parsed),
});

/** Idempotent boot loader — migrates the legacy `cnt-my-rv-profiles` key (and,
 *  failing that, the older single-RV `cnt-my-rv` key) into IndexedDB, then
 *  hydrates the synchronous read cache. Call once, from an `APP_INITIALIZER`. */
export async function initMyRvStore(): Promise<void> {
  await myRvStore.init();
  if (typeof window === 'undefined') return;
  // The multi-profile key migrated (or already had data) — the older
  // single-RV key is irrelevant, exactly like the pre-IDB readMyRvProfiles()
  // never consulted it once the multi-profile key parsed successfully.
  if (myRvStore.read(MY_RV_PROFILES_IDB_KEY) !== undefined) return;
  try {
    const legacyRaw = localStorage.getItem(MY_RV_KEY);
    if (legacyRaw) {
      const profile: IMyRvProfile = { ...normalizeRv(JSON.parse(legacyRaw) as Partial<IMyRv>), syncId: newRvId(), name: 'My RV' };
      const state: IMyRvProfilesState = { profiles: [profile], activeId: profile.syncId ?? null };
      await myRvStore.write(MY_RV_PROFILES_IDB_KEY, state);
      localStorage.removeItem(MY_RV_KEY);
    }
  } catch {
    // corrupt legacy data — leave empty
  }
}

export type { ITowVehicle };

/**
 * The "my garage" RV — a client-side-only, localStorage-backed vehicle record.
 * This IS an `IRV` from `@curbnturf/objects` (via `@curbnturf/entities`), not a parallel
 * shape: `type/length/height/width/year/make/model/licensePlate/photo/licensePhoto/
 * mpg/fuelTankGallons/towVehicle` all come straight from the shared interface. The one
 * local convention: unsynced profiles are identified by `syncId` (a client-generated
 * id, exactly what that field exists for) rather than the backend-assigned numeric `id`.
 */
export type IMyRv = IRV;

/** A named, saveable RV — a guest can keep several (a Class A, a teardrop, …). Same shape as IMyRv; `syncId`/`name` are always set once persisted. */
export type IMyRvProfile = IRV;

export interface IMyRvProfilesState {
  profiles: IMyRvProfile[];
  activeId: string | null;
}

export function emptyMyRv(): IMyRv {
  return {};
}

export function emptyTowVehicle(): ITowVehicle {
  return {};
}

function newRvId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'rv-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
}

export function emptyMyRvProfile(name = 'My RV'): IMyRvProfile {
  return { syncId: newRvId(), name };
}

/** Wraps a raw data-URL/asset-path string as an ISubPhoto until real photo upload/storage exists. */
export function photoFromDataUrl(dataUrl: string | null | undefined): ISubPhoto | undefined {
  return dataUrl ? { key: dataUrl } : undefined;
}

function normalizeTowVehicle(raw: unknown): ITowVehicle | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const t = raw as Partial<ITowVehicle>;
  return {
    year: typeof t.year === 'number' ? t.year : undefined,
    make: typeof t.make === 'string' ? t.make : undefined,
    model: typeof t.model === 'string' ? t.model : undefined,
    licensePlate: typeof t.licensePlate === 'string' ? t.licensePlate : undefined,
    length: typeof t.length === 'number' ? t.length : undefined,
    photo: t.photo && typeof t.photo === 'object' ? (t.photo as ISubPhoto) : undefined,
  };
}

/** Per-field coercion for anything read out of localStorage. */
function normalizeRv(parsed: Partial<IMyRv>): IMyRv {
  return {
    type: typeof parsed.type === 'string' ? parsed.type : undefined,
    length: typeof parsed.length === 'number' ? parsed.length : undefined,
    height: typeof parsed.height === 'number' ? parsed.height : undefined,
    width: typeof parsed.width === 'number' ? parsed.width : undefined,
    year: typeof parsed.year === 'number' ? parsed.year : undefined,
    make: typeof parsed.make === 'string' ? parsed.make : undefined,
    model: typeof parsed.model === 'string' ? parsed.model : undefined,
    licensePlate: typeof parsed.licensePlate === 'string' ? parsed.licensePlate : undefined,
    photo: parsed.photo && typeof parsed.photo === 'object' ? (parsed.photo as ISubPhoto) : undefined,
    licensePhoto: parsed.licensePhoto && typeof parsed.licensePhoto === 'object' ? (parsed.licensePhoto as ISubPhoto) : undefined,
    towVehicle: normalizeTowVehicle(parsed.towVehicle),
    mpg: typeof parsed.mpg === 'number' ? parsed.mpg : undefined,
    fuelTankGallons: typeof parsed.fuelTankGallons === 'number' ? parsed.fuelTankGallons : undefined,
  };
}

function normalizeProfile(p: Partial<IMyRvProfile>): IMyRvProfile {
  return {
    ...normalizeRv(p),
    syncId: typeof p.syncId === 'string' ? p.syncId : newRvId(),
    name: typeof p.name === 'string' && p.name.trim() ? p.name : 'My RV',
  };
}

/** Shapes a parsed `cnt-my-rv-profiles` legacy value into profiles state —
 *  same coercion `readMyRvProfiles()` used to do inline. Never throws (a
 *  non-object `parsed` just yields an empty profile list); the migration's
 *  own try/catch around `JSON.parse` is what guards against truly corrupt
 *  (unparseable) legacy data. */
function reviveMyRvProfilesState(parsed: unknown): IMyRvProfilesState {
  const p = (parsed ?? {}) as { profiles?: unknown; activeId?: unknown };
  const rawProfiles: unknown[] = Array.isArray(p.profiles) ? p.profiles : [];
  const profiles = rawProfiles
    .filter((x): x is Partial<IMyRvProfile> => !!x && typeof (x as { syncId?: unknown }).syncId === 'string')
    .map(normalizeProfile);
  let activeId = typeof p.activeId === 'string' ? p.activeId : null;
  if (!profiles.some(pr => pr.syncId === activeId)) activeId = profiles[0]?.syncId ?? null;
  return { profiles, activeId };
}

/** The single source of truth for RV profiles. Synchronous — reads the
 *  cache hydrated by `initMyRvStore()` (legacy-key migration happens there,
 *  once, at boot; see that function's doc comment). */
export function readMyRvProfiles(platformId: object): IMyRvProfilesState {
  if (!isPlatformBrowser(platformId)) return { profiles: [], activeId: null };
  return myRvStore.read(MY_RV_PROFILES_IDB_KEY) ?? { profiles: [], activeId: null };
}

/** Updates the cache immediately (synchronous reads right after this call
 *  see the new state); persists to IndexedDB in the background. On a write
 *  failure (storage full/unavailable — much rarer now than under
 *  `localStorage`'s ~5-10MB cap, but not impossible for a profile carrying
 *  several full-size photos) falls back to dropping photos from inactive
 *  profiles, then from all profiles, same as the original `localStorage`
 *  quota fallback. Note the cache briefly reflects the untrimmed state
 *  until a fallback retry lands — an accepted trade-off of keeping cache
 *  updates synchronous (see idb-kv-store.util.ts). */
export function writeMyRvProfiles(platformId: object, state: IMyRvProfilesState): void {
  if (!isPlatformBrowser(platformId)) return;
  if (state.profiles.length === 0) {
    void myRvStore.delete(MY_RV_PROFILES_IDB_KEY);
    return;
  }
  void myRvStore.write(MY_RV_PROFILES_IDB_KEY, state).catch(() => {
    const trimmed: IMyRvProfilesState = {
      activeId: state.activeId,
      profiles: state.profiles.map(p => p.syncId === state.activeId ? p : { ...p, photo: undefined, licensePhoto: undefined }),
    };
    void myRvStore.write(MY_RV_PROFILES_IDB_KEY, trimmed).catch(() => {
      const specsOnly: IMyRvProfilesState = {
        activeId: state.activeId,
        profiles: state.profiles.map(p => ({ ...p, photo: undefined, licensePhoto: undefined })),
      };
      void myRvStore.write(MY_RV_PROFILES_IDB_KEY, specsOnly);
    });
  });
}

export function listMyRvProfiles(platformId: object): IMyRvProfile[] {
  return readMyRvProfiles(platformId).profiles;
}

export function getActiveRvProfileId(platformId: object): string | null {
  return readMyRvProfiles(platformId).activeId;
}

export function getActiveRvProfile(platformId: object): IMyRvProfile | null {
  const state = readMyRvProfiles(platformId);
  return state.profiles.find(p => p.syncId === state.activeId) ?? null;
}

/** Appends a fresh, blank profile. Becomes active if it's the first one. */
export function addMyRvProfile(platformId: object, name = 'My RV', data?: Partial<IMyRvProfile>): IMyRvProfile {
  const state = readMyRvProfiles(platformId);
  // `data` carries the fields the host actually filled in, so a profile can be
  // created already-populated rather than created blank and immediately patched
  // (Batch A #9). The caller used to create on click and patch on save, which
  // left an empty, INCOMPLETE — and, for a first rig, ACTIVE — record behind
  // whenever the form was abandoned, and every booking gate reads the active
  // profile.
  const blank = emptyMyRvProfile(name);
  // Keep the caller's `syncId` when it brought one. The editor mints a working
  // copy the moment the form opens, and carrying that id through means the rig's
  // identity does not change between opening the form and saving it — the
  // alternative silently renames it mid-flight and breaks anything holding the
  // id it was shown. A blank id is minted here for callers that pass no data.
  const profile: IMyRvProfile = { ...blank, ...data, syncId: data?.syncId || blank.syncId };
  state.profiles.push(profile);
  if (!state.activeId) state.activeId = profile.syncId ?? null;
  writeMyRvProfiles(platformId, state);
  return profile;
}

/** Local-only merge, no backend sync — used both by the public `updateMyRvProfile`
 *  and internally to patch a profile's real `id`/`photo` back in after a sync
 *  completes (which must NOT itself re-trigger another sync). */
function patchMyRvProfileLocal(platformId: object, syncId: string, patch: Partial<IMyRv> & { name?: string }): IMyRvProfile | null {
  const state = readMyRvProfiles(platformId);
  const idx = state.profiles.findIndex(p => p.syncId === syncId);
  if (idx === -1) return null;
  const merged: IMyRvProfile = { ...state.profiles[idx], ...patch };
  if (!merged.name || !merged.name.trim()) merged.name = 'My RV';
  state.profiles[idx] = merged;
  writeMyRvProfiles(platformId, state);
  return merged;
}

/** Merges field changes into one profile by syncId, then pushes the result to
 *  the real backend in the background (no-op if signed out — see
 *  syncProfileToBackend). Name falls back to 'My RV' when blank. */
export function updateMyRvProfile(platformId: object, syncId: string, patch: Partial<IMyRv> & { name?: string }): void {
  const previous = readMyRvProfiles(platformId).profiles.find(p => p.syncId === syncId);
  const merged = patchMyRvProfileLocal(platformId, syncId, patch);
  if (merged) syncProfileToBackend(platformId, merged, previous);
}

/** Removes a profile; reassigns active to the first remaining one when needed.
 *  Also removes it from the real backend's IUser.rvs when it was ever synced. */
export function deleteMyRvProfile(platformId: object, syncId: string): void {
  const state = readMyRvProfiles(platformId);
  const removed = state.profiles.find(p => p.syncId === syncId);
  const profiles = state.profiles.filter(p => p.syncId !== syncId);
  let activeId = state.activeId;
  if (activeId === syncId || !profiles.some(p => p.syncId === activeId)) {
    activeId = profiles[0]?.syncId ?? null;
  }
  writeMyRvProfiles(platformId, { profiles, activeId });
  if (removed?.id) removeProfileFromBackend(platformId, removed.id);
}

export function setActiveRvProfile(platformId: object, syncId: string): void {
  const state = readMyRvProfiles(platformId);
  if (!state.profiles.some(p => p.syncId === syncId)) return;
  writeMyRvProfiles(platformId, { ...state, activeId: syncId });
}

/**
 * Reads the ACTIVE profile as a plain IMyRv. Back-compat shim: every existing
 * consumer that called readMyRv() keeps working — it now sees the active rig.
 */
export function readMyRv(platformId: object): IMyRv {
  const active = getActiveRvProfile(platformId);
  // normalizeRv copies only the IMyRv fields — drops syncId/name cleanly.
  return active ? normalizeRv(active) : emptyMyRv();
}

/**
 * Writes IMyRv fields into the ACTIVE profile (back-compat shim). If there is
 * no active profile yet and the data is non-empty, lazily materializes one so
 * first-time callers (e.g. the booking widget photo-attach) still "just work".
 * Clearing every field no longer deletes the profile — deletion is explicit.
 */
export function writeMyRv(platformId: object, rv: IMyRv): void {
  if (!isPlatformBrowser(platformId)) return;
  const state = readMyRvProfiles(platformId);
  const activeId = state.activeId;
  const idx = activeId ? state.profiles.findIndex(p => p.syncId === activeId) : -1;

  if (idx === -1) {
    const isEmpty = !isMyRvSet(rv) && !rv.photo && !rv.licensePhoto;
    if (isEmpty) { writeMyRvProfiles(platformId, state); return; }
    const fresh: IMyRvProfile = { ...emptyMyRv(), ...rv, syncId: newRvId(), name: 'My RV' };
    state.profiles.push(fresh);
    state.activeId = fresh.syncId ?? null;
    writeMyRvProfiles(platformId, state);
    syncProfileToBackend(platformId, fresh);
    return;
  }

  const previous = state.profiles[idx];
  const merged = { ...previous, ...rv };
  state.profiles[idx] = merged;
  writeMyRvProfiles(platformId, state);
  syncProfileToBackend(platformId, merged, previous);
}

/** Lightweight "anything filled" check — used for UI hints. */
export function isMyRvSet(rv: IMyRv): boolean {
  return !!(rv.type || rv.length || rv.height || rv.width || rv.year || rv.make || rv.model || rv.licensePlate);
}

/** Strict completeness check — every field a host needs to confirm fit. Required to book any listing. */
export function isMyRvComplete(rv: IMyRv): boolean {
  return !!(
    rv.type &&
    typeof rv.length === 'number' && rv.length > 0 &&
    typeof rv.height === 'number' && rv.height > 0 &&
    typeof rv.width === 'number' && rv.width > 0 &&
    rv.licensePlate && rv.licensePlate.trim().length > 0
  );
}

/** Itemized list of the fields still missing for full completeness — drives error copy. */
export function myRvMissingFields(rv: IMyRv): string[] {
  const missing: string[] = [];
  if (!rv.type) missing.push('RV type');
  if (!(typeof rv.length === 'number' && rv.length > 0)) missing.push('length');
  if (!(typeof rv.height === 'number' && rv.height > 0)) missing.push('height');
  if (!(typeof rv.width === 'number' && rv.width > 0)) missing.push('width');
  if (!(rv.licensePlate && rv.licensePlate.trim().length > 0)) missing.push('license plate');
  return missing;
}

/** True when both required photos are attached to the My RV profile. */
export function hasMyRvPhotos(rv: IMyRv): boolean {
  return !!(rv.photo && rv.licensePhoto);
}

export function rvTypeLabel(type: string | null | undefined): string {
  if (!type) return 'RV';
  return RV_TYPES.find(t => t.id === type)?.label ?? 'RV';
}

/** Rig types that are towed and so have a separate tow vehicle. */
const TOWABLE_RV_TYPES: RvType[] = ['fifthWheel', 'travelTrailer', 'teardrop', 'popup'];

export function isTowableRv(type: string | null | undefined): boolean {
  return !!type && TOWABLE_RV_TYPES.includes(type as RvType);
}

/** True when a tow vehicle carries any entered detail. */
export function towVehicleHasData(t: ITowVehicle | null | undefined): boolean {
  return !!t && !!(t.year || t.make || t.model || t.licensePlate || t.length || t.photo);
}
